/**
 * <s-product-gallery> — one-image-at-a-time product media carousel with thumbnails.
 *
 * Native snap scrolling does the sliding (swipe, trackpad, keyboard scroll all work
 * without JS). This component only syncs the controls with the scroll position.
 *
 * Commands (dispatched via commandfor/command on buttons targeting this element):
 *   --prev / --next          step one slide
 *   --go                     jump to the slide in the button's data-gallery-index
 *
 * Required markup:
 *   [data-gallery-track]     horizontal snap-scroll container
 *   [data-gallery-slide]     one per media item, direct children of the track
 * Optional markup:
 *   [data-gallery-thumb][data-gallery-index]   thumbnail buttons (aria-current is managed)
 *   [data-gallery-prev] / [data-gallery-next]  arrow buttons (disabled at the ends)
 *   [data-gallery-current]                     text node for the 1-based current index
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #index = 0;
  #frame = 0;
  // While an arrow/thumbnail click is gliding to its target, ignore the in-between scroll
  // positions so the counter and thumbnails jump straight to the destination.
  #gliding = false;
  #settleTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.querySelectorAll('[data-gallery-slide]'));

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#track.addEventListener('keydown', this.#handleKeydown, opts);

    // Variant → media map, rendered as JSON in the gallery.
    this.#variantMedia = {};
    try {
      this.#variantMedia = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '{}');
    } catch (_) {
      this.#variantMedia = {};
    }
    document.addEventListener('variant:selected', this.#handleVariantSelected, opts);

    const initialIndex = this.#indexForMedia(this.dataset.initialMediaId);
    if (initialIndex > 0) {
      // Deep link with ?variant=: open on that variant's photo without animating.
      requestAnimationFrame(() => this.goTo(initialIndex, true));
    } else {
      this.#sync(0);
    }
  }

  #variantMedia = {};

  #indexForMedia(mediaId) {
    if (!mediaId) return -1;
    return this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
  }

  #handleVariantSelected = (event) => {
    const { productId, variantId } = event.detail || {};
    if (productId && this.dataset.productId && String(productId) !== this.dataset.productId) return;
    const index = this.#indexForMedia(this.#variantMedia[String(variantId)]);
    if (index >= 0) this.goTo(index);
  };

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settleTimer);
  }

  #handleCommand = (event) => {
    if (event.command === '--prev') this.goTo(this.#index - 1);
    else if (event.command === '--next') this.goTo(this.#index + 1);
    else if (event.command === '--go') {
      const index = Number(event.source?.dataset.galleryIndex);
      if (Number.isInteger(index)) this.goTo(index);
    }
  };

  #handleKeydown = (event) => {
    if (event.target !== this.#track) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.#index + (rtl ? -1 : 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.#index + (rtl ? 1 : -1));
    }
  };

  #currentScrollIndex() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #handleScroll = () => {
    if (this.#gliding) {
      // Programmatic glide in progress: wait until scrolling stops, then settle once.
      clearTimeout(this.#settleTimer);
      this.#settleTimer = setTimeout(() => {
        this.#gliding = false;
        const index = this.#currentScrollIndex();
        if (index !== this.#index) this.#sync(index);
      }, 120);
      return;
    }
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const index = this.#currentScrollIndex();
      if (index !== this.#index) this.#sync(index);
    });
  };

  goTo(index, instant = false) {
    if (!this.#slides.length) return;
    const clamped = Math.max(0, Math.min(this.#slides.length - 1, index));
    const reduceMotion = instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const offset = clamped * this.#track.clientWidth;
    this.#sync(clamped);
    if (clamped === this.#currentScrollIndex()) return;
    this.#gliding = !reduceMotion;
    this.#track.scrollTo({ left: rtl ? -offset : offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  // Start loading the neighbouring photos early so swiping never waits on a blank slide.
  #warm(index) {
    for (const i of [index - 1, index + 1, index + 2]) {
      const img = this.#slides[i]?.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    }
  }

  #sync(index) {
    this.#index = index;
    const last = this.#slides.length - 1;

    // Only write to the slides whose state actually changes, avoiding needless re-styling.
    this.#slides.forEach((slide, i) => {
      const shouldBeInert = i !== index;
      if (slide.inert !== shouldBeInert) slide.inert = shouldBeInert;
    });
    this.#warm(index);

    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const active = Number(thumb.dataset.galleryIndex) === index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        // Scroll only the thumbnail rail, never the page.
        const rail = thumb.closest('[data-gallery-thumbs]');
        if (rail && rail.scrollWidth > rail.clientWidth) {
          rail.scrollTo({ left: thumb.offsetLeft - (rail.clientWidth - thumb.clientWidth) / 2 });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    }

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= last;

    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(index + 1);
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
